import * as THREE from "three";
import { PALETTE, VFX } from "../../../../../art/vfx.js";
import type { Ribbon, VfxLightSlot } from "../../../../../art/vfx.js";
import { AnimRig } from "../rig";
import { lib } from "./common";
import { ARROW_SCALE, Ballistics, releaseSeconds } from "./shotTable";

export { RELEASE_FPS, TOWER_SHOT, UNIT_SHOT, releaseSeconds, type Ballistics } from "./shotTable";

interface Shot {
  obj: THREE.Object3D; rig: AnimRig; model: string; kind: string;
  from: THREE.Vector3; to: THREE.Vector3; target: (() => THREE.Vector3 | null) | null;
  t: number; dur: number; arc: number; scale: number;
  /** The caller's `scale` (the element towers' tier size), passed on to the muzzle and impact dressing. */
  fx: number;
  /** Distance still to travel before the next trail emission (trails are emitted by distance, vfx.md F4). */
  toNext: number;
  /** A fireball's travelling VFX light slot, and the seed that proves the slot is still ours. */
  light: VfxLightSlot | null; lightSeed: number;
  /** Its ribbon trail in the VFX module's merged ribbon layer (arrows, bolts, fireballs, shards, globs). */
  ribbon: Ribbon | null;
  handle: ShotHandle | null;
  /** A piercing round's victims, by where along the flight (0–1) it passes each; `hit` once its impact has played. */
  passes: Pass[];
  /** Radians the round has tumbled end over end (ModelSpin). */
  spin: number;
  /** The sideways bend of a round that left along a barrel off its line (fades out by the landing; see `bend`). */
  bend: THREE.Vector3;
  /** Drawn by the VFX sprites instead of its model (SpriteRounds), and the seed that keeps two such rounds apart. */
  sprite: boolean; seed: number;
}
/** How a shot is fired beyond its Ballistics (ProjectileViews.fire). */
export interface FireOptions {
  /** The rate the shooter's firing clip plays at (towers.ts towerFireRate): the release frame comes that much sooner. */
  readonly rate?: number;
  /**
   * The enemies a piercing round goes through (the ballista: the sim's hits of the tick, nearest first). The round
   * flies past the farthest one into the ground and plays an impact on each as it passes.
   */
  readonly through?: readonly THREE.Vector3[];
  /** Seconds more before the release (the weapon still coming round: views/towerAim.ts releaseDelay). */
  readonly delay?: number;
  /**
   * Which way the weapon points at the release (world, read then): a round whose barrel is still off the line to its
   * target leaves along the barrel and bends onto that line (`bend`), instead of leaving sideways out of the muzzle.
   */
  readonly barrel?: () => THREE.Vector3 | null;
}
interface Pending { at: number; model: string; fire: () => void }
/** A piercing round's pass by one victim: where along the flight (0–1), where, whether it has played, and `i` its index in `through`. */
interface Pass { t: number; at: THREE.Vector3; hit: boolean; i?: number }
/**
 * Returned by `fire`: set `onImpact` to run something when (if) the round lands — e.g. a hit-stop on a multi-kill —
 * and `onPass` for each victim a piercing round passes (its index in `FireOptions.through`).
 */
export interface ShotHandle { onImpact: (() => void) | null; onPass?: ((index: number) => void) | null }

/**
 * Real projectiles (art/DEV_HANDOFF.md §2): a `proj_*` model per shot, spawned on the firing clip's release frame,
 * flown to the target on a straight line or a parabola with its `fly` clip looping, dressed by the VFX module's
 * muzzle / trail / impact. Clones are pooled per model because each needs its own mixer. Presentation only — the
 * sim has already resolved the hit; the round just arrives where the damage went.
 */
export class ProjectileViews {
  readonly group = new THREE.Group();
  private readonly live: Shot[] = [];
  private readonly pool = new Map<string, Shot[]>();
  private readonly pending: Pending[] = [];
  /** Rounds of each model waiting for their release (bounded by `MaxPendingPerModel`). */
  private readonly pendingOf = new Map<string, number>();
  private readonly due: Pending[] = [];
  private readonly forward = new THREE.Vector3(0, 0, -1);
  private clock = 0;
  static readonly MaxLive = 48;
  static readonly MaxPerModel = 12;
  /**
   * Rounds of one model that may wait for their release at once (M34). At most `MaxPerModel` fly, so more than this
   * waiting would only be dropped at launch — but they used to queue anyway: a late wave's healers send a charm per
   * heal (~12 a tick at ×3), and under hit-stop's slowed clock the queue reached 5,000–10,000 entries, each launch
   * then spliced out of the middle of it.
   */
  static readonly MaxPendingPerModel = 2 * ProjectileViews.MaxPerModel;
  /**
   * Every round is drawn 1.6× its modelled size (vfx.md F10): at the default zoom a fireball was ~6 px and a frost
   * shard ~5 px on a phone — the shot was only readable by its trail.
   */
  static readonly Scale = 1.6;
  /**
   * ...except where 1.6× outgrows the gun: a cannonball that size (0.29 m) was wider than the whole barrel and seemed
   * to hang beside it rather than leave it (owner report 2026-09-23). It is drawn the barrel's width instead.
   */
  static readonly ModelScale: Readonly<Record<string, number>> = { proj_cannonball: 1.1, proj_arrow: ARROW_SCALE };
  /** Rounds that tumble end over end in flight (rad/s about their side axis): a cannonball that only slid read as a decal. */
  static readonly ModelSpin: Readonly<Record<string, number>> = { proj_cannonball: 14 };
  /**
   * Rounds the VFX draws instead of their model while the VFX runs: the fireball (vfx.js `fireball`, FIRE). Its GLB is
   * five flame cards and five glow shells in one flat emissive colour, which read on a phone as a pale orange blob;
   * the sprites give it a white-hot core, a hot flame body trailing the flight and a red heat halo. The model stays
   * for reduced effects and until the atlas has loaded.
   */
  static readonly SpriteRounds: ReadonlySet<string> = new Set(["proj_fireball"]);
  /** No round's flight is shorter than this (s), however close its target: it must be seen to leave and land. */
  static readonly MinFlightSeconds = 0.08;
  /** A piercing round carries on this far (world units) past its last victim before it buries itself. */
  static readonly PierceOvershoot = 0.6;
  /** How far below its last victim's aim point a piercing round may bury itself (the aim point's lift off the ground). */
  static readonly PierceSink = 0.12;
  /**
   * A round leaves the muzzle at `LaunchScale` of its size and grows to full over `LaunchGrowSeconds`: every round is
   * wider than the bore it leaves (a cannonball twice the `bore` disc), and at full size on its first frame it seemed
   * to appear beside the barrel rather than come out of it.
   */
  static readonly LaunchScale = 0.4;
  /**
   * ...except a round the weapon showed loaded (the nocked arrow, the ballista's bolt): it leaves at the loaded
   * round's size, so the flying one continues the one that vanished from the bow instead of swelling out of it.
   */
  static readonly LaunchScaleOf: Readonly<Record<string, number>> = { proj_arrow: 1, proj_bolt: 0.9 };
  static readonly LaunchGrowSeconds = 0.09;
  /**
   * A lob reaches its full `arc` at this range (world units) and scales down below it, so a short shot leaves at the
   * same climb as a long one: a fixed 0.9 m cannon lob at two cells left the barrel at ~55° while the barrel sat level.
   */
  static readonly ArcFullRange = 4;
  /** At most this many trail emissions per round per frame (a hitch must not dump a burst of smoke). */
  static readonly MaxTrailPerFrame = 8;
  private readonly prev = new THREE.Vector3();
  private readonly vel = new THREE.Vector3();
  private readonly dir = new THREE.Vector3();
  private readonly at = new THREE.Vector3();

  constructor(private readonly vfx: () => VFX | null, private readonly reduced: () => boolean) {}

  /**
   * Queue a shot for `b.release` frames from now. `from` is read lazily at release so the recoiling muzzle node is in
   * the right place; `target` is re-read each frame so the round lands on a moving enemy.
   */
  fire(b: Ballistics, from: () => THREE.Vector3 | null, target: () => THREE.Vector3 | null, fallbackFrom: THREE.Vector3, scale = 1, opts: FireOptions = {}): ShotHandle {
    const handle: ShotHandle = { onImpact: null };
    const waiting = this.pendingOf.get(b.model) ?? 0;
    if (waiting >= ProjectileViews.MaxPendingPerModel) return handle;        // it could not fly anyway (see the cap)
    this.pendingOf.set(b.model, waiting + 1);
    this.pending.push({ at: this.clock + releaseSeconds(b, opts.rate) + Math.max(0, opts.delay ?? 0), model: b.model, fire: () => {
      const L = lib();
      if (!L || !L.hq(b.model)) return;
      const to = target() ?? null;
      if (!to) return;
      const start = from() ?? fallbackFrom;
      if (this.live.length >= ProjectileViews.MaxLive || this.countOf(b.model) >= ProjectileViews.MaxPerModel) return;
      const shot = this.acquire(b.model);
      shot.kind = b.kind; shot.scale = scale * (ProjectileViews.ModelScale[b.model] ?? ProjectileViews.Scale); shot.fx = scale;
      shot.from.copy(start); shot.to.copy(to); shot.target = target;
      shot.passes.length = 0; shot.spin = 0;
      if (opts.through?.length) { shot.target = null; ProjectileViews.pierceLine(start, to, opts.through, ProjectileViews.PierceOvershoot, shot.to, shot.passes); }
      const dist = start.distanceTo(shot.to);
      shot.t = 0; shot.toNext = 0; shot.handle = handle;
      shot.arc = b.launch !== undefined ? ProjectileViews.lobFor(start, shot.to, b.launch) : b.arc * Math.min(1, dist / ProjectileViews.ArcFullRange);
      shot.dur = Math.max(ProjectileViews.MinFlightSeconds, dist / b.speed);
      ProjectileViews.bend(start, shot.to, opts.barrel?.() ?? null, shot.bend);
      const v = this.vfx();
      shot.sprite = !!v && !this.reduced() && ProjectileViews.SpriteRounds.has(b.model); shot.seed = Math.random();
      shot.obj.position.copy(start); shot.obj.scale.setScalar(shot.scale * (ProjectileViews.LaunchScaleOf[b.model] ?? ProjectileViews.LaunchScale)); shot.obj.visible = !shot.sprite;
      const launch = ProjectileViews.launchDir(start, shot.to, shot.arc, this.dir).add(shot.bend);
      this.aim(shot, launch);
      // the muzzle flash points the way the round leaves
      v?.muzzle(b.kind, start, this.at.copy(start).add(launch), scale);
      // the fireball carries a small warm light with it (vfx.md F5.1: r 1.2)
      shot.light = null;
      if (v && b.kind === "fire" && !this.reduced()) { shot.light = v.light(start, PALETTE.fire.rim, 1.2, 1.6, shot.dur + 0.05); shot.lightSeed = shot.light.seed; }
      shot.ribbon = v && !this.reduced() ? v.ribbon(b.kind, start) : null;
      this.live.push(shot);
    } });
    return handle;
  }

  update(dt: number): void {
    this.clock += dt;
    const v = this.vfx();
    const reduced = this.reduced();
    for (let i = this.live.length - 1; i >= 0; i--) {
      const s = this.live[i];
      s.rig.update(dt);
      const fresh = s.target?.();
      if (fresh) s.to.copy(fresh);
      s.t = Math.min(1, s.t + dt / s.dur);
      s.obj.scale.setScalar(s.scale * ProjectileViews.launchGrow(s.t * s.dur, ProjectileViews.LaunchScaleOf[s.model]));
      const prev = this.prev.copy(s.obj.position);
      s.obj.position.lerpVectors(s.from, s.to, s.t);
      if (s.arc > 0) s.obj.position.y += s.arc * 4 * s.t * (1 - s.t);
      if (s.bend.x !== 0 || s.bend.z !== 0) s.obj.position.addScaledVector(s.bend, ProjectileViews.bendAt(s.t));
      const step = this.vel.subVectors(s.obj.position, prev);
      const len = step.length();
      if (len > 1e-4) this.aim(s, step);
      // at the round's drawn size over its modelled 1.6×: the element tier scale, times the launch growth
      if (s.sprite) v?.fireball(s.obj.position, step, s.obj.scale.x / ProjectileViews.Scale, this.clock, s.seed);
      const spin = ProjectileViews.ModelSpin[s.model];
      if (spin) { s.spin += spin * dt; s.obj.rotateX(-s.spin); }
      for (const p of s.passes) if (!p.hit && s.t >= p.t) { p.hit = true; v?.impact(s.kind, p.at, Math.atan2(step.x, step.z)); if (p.i !== undefined) s.handle?.onPass?.(p.i); }
      if (s.light && s.light.seed === s.lightSeed) { s.light.x = s.obj.position.x; s.light.y = s.obj.position.y; s.light.z = s.obj.position.z; }
      if (v && !reduced && len > 1e-6) this.emitTrail(v, s, prev, len, dt);
      if (s.ribbon) v?.ribbonPush(s.ribbon, s.obj.position);
      if (s.t >= 1) {
        v?.impact(s.kind, s.to, Math.atan2(step.x, step.z), s.fx);
        s.handle?.onImpact?.();
        this.live.splice(i, 1);
        this.release(s);
      }
    }
    // Rounds due this frame launch after the flight step: their first frame is drawn at the muzzle. Stepped at once, a
    // cannonball was already a frame along its lob — ~0.3 m above the barrel — the first time it was seen.
    // One pass: the due rounds are taken out (latest-queued first, as before) and the rest close up in order.
    const q = this.pending, due = this.due;
    let w = 0;
    for (let i = 0; i < q.length; i++) { const p = q[i]; if (p.at <= this.clock) due.push(p); else q[w++] = p; }
    q.length = w;
    for (let i = due.length - 1; i >= 0; i--) { const p = due[i]; this.pendingOf.set(p.model, (this.pendingOf.get(p.model) ?? 1) - 1); p.fire(); }
    due.length = 0;
  }

  /**
   * Trail emissions by distance, not by frame (vfx.md F4): one every `VFX.TRAIL_SPACING[kind]` units along this
   * frame's segment, so 30 Hz leaves no gaps and 120 Hz doesn't double the smoke.
   */
  private emitTrail(v: VFX, s: Shot, from: THREE.Vector3, len: number, dt: number): void {
    const spacing = (VFX.TRAIL_SPACING as Record<string, number>)[s.kind] ?? 0.1;
    this.dir.copy(this.vel).multiplyScalar(1 / len);
    const speed = this.vel.multiplyScalar(1 / Math.max(dt, 1e-3));      // world velocity, points the streaks
    let along = s.toNext, n = 0;
    while (along <= len && n < ProjectileViews.MaxTrailPerFrame) {
      v.trail(s.kind, this.at.copy(from).addScaledVector(this.dir, along), speed);
      along += spacing; n++;
    }
    s.toNext = Math.max(0, along - len);
  }

  /** Barrels further off the round's line than this (radians) are not followed: the round just flies its line. */
  static readonly MaxBend = 0.9;
  /** Off by less than this (radians), the round flies its line as it is. */
  static readonly MinBend = 0.03;

  /**
   * The bend of a round leaving `from` for `to` along `barrel` (world direction, or null): the horizontal offset `out`
   * whose `bendAt` share is added to the straight flight, so the flight's first tangent points along the barrel
   * (bendAt′(0) = 1) and the round still lands on `to` (bendAt(1) = 0). Zero when the barrel is on the line, far off it
   * (> MaxBend: a weapon that never came round), or pointing nowhere. Pure.
   */
  static bend(from: THREE.Vector3, to: THREE.Vector3, barrel: THREE.Vector3 | null, out: THREE.Vector3): THREE.Vector3 {
    out.set(0, 0, 0);
    if (!barrel) return out;
    const cx = to.x - from.x, cz = to.z - from.z, d = Math.hypot(cx, cz), bl = Math.hypot(barrel.x, barrel.z);
    if (d < 1e-3 || bl < 1e-3) return out;
    const off = Math.abs(Math.atan2(cx * barrel.z - cz * barrel.x, cx * barrel.x + cz * barrel.z));
    if (off < ProjectileViews.MinBend || off > ProjectileViews.MaxBend) return out;
    return out.set((barrel.x / bl) * d - cx, 0, (barrel.z / bl) * d - cz);
  }
  /** The share of the bend `t` (0–1) into the flight: t·(1 − t)², slope 1 at the muzzle, gone on landing. */
  static bendAt(t: number): number { return t * (1 - t) * (1 - t); }

  /** Scale factor of a round `seconds` into its flight: `from` (LaunchScale) → 1, easing out. */
  static launchGrow(seconds: number, from = ProjectileViews.LaunchScale): number {
    const k = Math.min(1, Math.max(0, seconds / ProjectileViews.LaunchGrowSeconds));
    return from + (1 - from) * (1 - (1 - k) * (1 - k));
  }

  /**
   * The lob (metres over the straight line at mid-flight) that makes a round leave `from` at `elevation` radians and
   * land on `to`: the flight is lerp(from, to, t) + 4·arc·t(1−t), whose slope at t = 0 is (Δy + 4·arc) / d.
   * Never below the straight line: a round aimed higher than its barrel still flies straight.
   */
  static lobFor(from: THREE.Vector3, to: THREE.Vector3, elevation: number): number {
    const d = Math.hypot(to.x - from.x, to.z - from.z);
    return Math.max(0, (d * Math.tan(elevation) - (to.y - from.y)) / 4);
  }

  /** The direction a round leaves in: the flight's tangent at t = 0 (the chord, lifted by the lob). */
  static launchDir(from: THREE.Vector3, to: THREE.Vector3, arc: number, out = new THREE.Vector3()): THREE.Vector3 {
    return out.subVectors(to, from).setY(to.y - from.y + 4 * arc);
  }

  /**
   * A piercing round's line: aimed at the farthest of its victims (by distance from `from` along the ground), carried
   * on `overshoot` past it into `end`; `passes` gets each victim at the fraction of the flight where the round is
   * nearest it, in flight order.
   */
  static pierceLine(from: THREE.Vector3, aim: THREE.Vector3, through: readonly THREE.Vector3[], overshoot: number, end: THREE.Vector3, passes: Pass[]): void {
    const flat = (p: THREE.Vector3) => Math.hypot(p.x - from.x, p.z - from.z);
    let far = aim;
    for (const p of through) if (flat(p) > flat(far)) far = p;
    const len = Math.max(1e-3, from.distanceTo(far));
    end.subVectors(far, from).multiplyScalar(1 + overshoot / len).add(from);
    // ...but no deeper than the ground under the last victim: a steep shot off a high bench otherwise drove the bolt
    // most of a metre underground (the victims stand PierceSink over the ground, like every round's target)
    end.y = Math.max(end.y, far.y - ProjectileViews.PierceSink);
    const line = new THREE.Vector3().subVectors(end, from), l2 = Math.max(1e-6, line.lengthSq());
    through.forEach((p, i) => passes.push({ t: Math.min(1, Math.max(0, new THREE.Vector3().subVectors(p, from).dot(line) / l2)), at: p.clone(), hit: false, i }));
    passes.sort((a, b) => a.t - b.t);
  }

  dispose(): void {
    for (const s of this.live) this.group.remove(s.obj);
    this.live.length = 0; this.pending.length = 0; this.pendingOf.clear();
    for (const list of this.pool.values()) for (const s of list) this.group.remove(s.obj);
    this.pool.clear();
  }

  private readonly aimDir = new THREE.Vector3();
  private aim(s: Shot, dir: THREE.Vector3): void {
    s.obj.quaternion.setFromUnitVectors(this.forward, this.aimDir.copy(dir).normalize());
  }

  /** Rounds waiting for their release (tests, the perf probes). */
  get pendingCount(): number { return this.pending.length; }

  private countOf(model: string): number { let n = 0; for (const s of this.live) if (s.model === model) n++; return n; }

  private acquire(model: string): Shot {
    const list = this.pool.get(model);
    const spare = list?.pop();
    if (spare) return spare;
    const L = lib()!;
    const obj = L.clone(`hq:${model}`);
    const rig = new AnimRig(obj, L.clipsOf(`hq:${model}`));
    rig.playLoop("fly", 1, true);
    this.group.add(obj);
    return { obj, rig, model, kind: "", from: new THREE.Vector3(), to: new THREE.Vector3(), target: null, t: 0, dur: 1, arc: 0, scale: 1, fx: 1, toNext: 0, light: null, lightSeed: 0, ribbon: null, handle: null, passes: [], spin: 0, sprite: false, seed: 0, bend: new THREE.Vector3() };
  }

  private release(s: Shot): void {
    s.obj.visible = false; s.target = null; s.handle = null;
    if (s.ribbon) { this.vfx()?.ribbonEnd(s.ribbon); s.ribbon = null; }
    if (s.light && s.light.seed === s.lightSeed) s.light.on = false;
    s.light = null;
    let list = this.pool.get(s.model);
    if (!list) { list = []; this.pool.set(s.model, list); }
    list.push(s);
  }
}
